import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Wordmark } from '../components/Wordmark';
import { api } from '../lib/api';
import { restoreSession, setSession, type SessionUser } from '../lib/auth';

type SessionResponse = {
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
  user: SessionUser;
};

type State = 'verifying' | 'error';

// The landing for both sign-in methods:
//   • magic link  → arrives with ?token=…, which we exchange here.
//   • Google/Apple OAuth → arrives with NO token; the backend already
//     established the session via the sm_refresh cookie on its redirect, so we
//     just re-issue an access token from it (restoreSession).
// Either way we then route to where the user was headed (sm_next, stashed by
// RequireSession) or to their diary at /app — no dead-end confirmation screen.
export function AuthCallback() {
  const [state, setState] = useState<State>('verifying');
  const [message, setMessage] = useState<string>('');
  const { route } = useLocation();

  useEffect(() => {
    const token = new URL(window.location.href).searchParams.get('token');
    (async () => {
      try {
        if (token) {
          const sr = await api<SessionResponse>('/api/auth/magic-link/verify', {
            method: 'POST',
            body: JSON.stringify({ token }),
          });
          setSession(sr.accessToken, sr.user);
        } else {
          // OAuth cookie landing — no token in the URL by design.
          const user = await restoreSession();
          if (!user) throw new Error("Sign-in didn't complete. Please try again.");
        }

        let dest = '/app';
        try {
          const next = localStorage.getItem('sm_next');
          localStorage.removeItem('sm_next');
          if (next && next.startsWith('/')) dest = next;
        } catch { /* private mode — fall through to default */ }
        route(dest, true);
      } catch (e) {
        setState('error');
        setMessage(e instanceof Error ? e.message : 'Verification failed');
      }
    })();
  }, []);

  return (
    <main class="page-in" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '24px 24px' }}>
        <a href="/" style={{ textDecoration: 'none', display: 'inline-flex' }}>
          <Wordmark size={26} />
        </a>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div
          style={{
            background: 'var(--card)', borderRadius: 28, padding: 40,
            boxShadow: 'var(--shadow-card)', maxWidth: 480, width: '100%', textAlign: 'center',
          }}
        >
          {state === 'verifying' && (
            <>
              <h1 class="wordmark" style={{ margin: 0, fontSize: 36, letterSpacing: '-0.02em' }}>
                Signing you in…
              </h1>
              <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>One moment.</p>
            </>
          )}
          {state === 'error' && (
            <>
              <h1 class="wordmark" style={{ margin: 0, fontSize: 32, color: 'var(--wine)', letterSpacing: '-0.02em' }}>
                Couldn't verify.
              </h1>
              <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>{message}</p>
              <p style={{ marginTop: 16 }}>
                <a href="/auth" style={{ color: 'var(--ember)', fontWeight: 600 }}>
                  Try again →
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
